import React, { useEffect, useState } from "react";
import { Download, X } from "lucide-react";
import { Button } from "@/components/ui/button";

const DISMISS_KEY = "cse-pwa-install-dismissed";

/**
 * Listens for the beforeinstallprompt event (Chrome / Edge / Samsung Internet)
 * and shows a small banner after the user has been on-page for 30 seconds.
 * On iOS the API isn't available; we render an iOS-specific hint instead.
 */
const PWAInstallPrompt = () => {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    const dismissed = localStorage.getItem(DISMISS_KEY);
    if (dismissed) return;

    const handler = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    window.addEventListener("beforeinstallprompt", handler);

    // Detect iOS Safari (no beforeinstallprompt support)
    const ua = window.navigator.userAgent.toLowerCase();
    const isIos = /iphone|ipad|ipod/.test(ua);
    const isStandalone = window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone;
    if (isIos && !isStandalone) {
      setIosHint(true);
    }

    const t = setTimeout(() => setShow(true), 30000);
    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      clearTimeout(t);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferred) return;
    deferred.prompt();
    const { outcome } = await deferred.userChoice;
    if (outcome === "accepted") {
      setDeferred(null);
      setShow(false);
      localStorage.setItem(DISMISS_KEY, "installed");
    }
  };

  const dismiss = () => {
    setShow(false);
    setIosHint(false);
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
  };

  if (!show && !iosHint) return null;
  if (!deferred && !iosHint) return null;

  return (
    <div
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[200] max-w-md w-[calc(100%-2rem)] rounded-lg border border-border bg-card shadow-lg p-3 flex items-center gap-3"
      data-testid="pwa-install-banner"
    >
      <Download className="h-5 w-5 text-primary shrink-0" />
      <div className="flex-1 text-xs">
        {deferred ? (
          <>
            <div className="font-medium text-sm">Install CSE Investor</div>
            <div className="text-muted-foreground">Add to your home screen for quick access.</div>
          </>
        ) : (
          <>
            <div className="font-medium text-sm">Add to Home Screen</div>
            <div className="text-muted-foreground">Tap the share icon ⎙ then "Add to Home Screen".</div>
          </>
        )}
      </div>
      {deferred && (
        <Button size="sm" onClick={handleInstall} data-testid="pwa-install-button">
          Install
        </Button>
      )}
      <button
        onClick={dismiss}
        className="text-muted-foreground hover:text-foreground p-1 rounded"
        aria-label="Dismiss"
        data-testid="pwa-install-dismiss"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
};

export default PWAInstallPrompt;
